import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { Observable, lastValueFrom, map, filter } from 'rxjs';

import { LoginUAEPassUser, LoginUser } from '../models/login-user';
import { LoggedInUser, User } from '../models/user';

import { ApiConfiguration } from '../configs/api-configuration';
import { BaseApiClient } from '../configs/base-api-client';
import { RequestBuilder } from '../helpers/request-builder';
import { StrictHttpResponse } from '../helpers/strict-http-response';
import { NewUser } from '../models/new-user';

@Injectable({
    providedIn: 'root',
})
export class UserAndAuthenticationApiClientService extends BaseApiClient {
    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        const config = inject(ApiConfiguration);
        const http = inject(HttpClient);

        super(config, http);
    }

    /**
     * Path part for operation login
     */
    static readonly LoginPath = '/login/';
    static readonly UAEPassLoginPath = 'uae-pass/login/';

    static readonly loginPaths = {
        login: '/login/',
        UAEPass: '/uae-pass/login/',
    };

    /**
     * Existing user login.
     *
     * Login for existing user
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `login()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    login$Response(
        params: {
            body;
        },
        key: string,
        context?: HttpContext,
    ): Observable<StrictHttpResponse<{ session_key: string }>> {
        const rb = new RequestBuilder(
            this.rootUrl,
            UserAndAuthenticationApiClientService.loginPaths[key],
            'post',
        );
        if (params) {
            rb.body(params.body, 'application/json');
        }

        return this.http
            .request(
                rb.build({
                    responseType: 'json',
                    accept: 'application/json',
                    context: context,
                }),
            )
            .pipe(
                filter((r) => r instanceof HttpResponse),
                map((r) => {
                    return r as StrictHttpResponse<{
                        session_key: string;
                    }>;
                }),
            );
    }

    /**
     * Existing user login.
     *
     * Login for existing user
     *
     * This method provides access only to the response body.
     * To access the full response (for headers, for example), `login$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    login(
        params: {
            body: LoginUser;
        },
        key: string,
        context?: HttpContext,
    ): Promise<{ session_key: string }> {
        return lastValueFrom(
            this.login$Response(params, key, context).pipe(
                map(
                    (
                        r: StrictHttpResponse<{
                            session_key: string;
                        }>,
                    ) =>
                        r.body as {
                            session_key: string;
                        },
                ),
            ),
        );
    }

    uaePassLogin(
        params: {
            body: LoginUAEPassUser;
        },
        key: string,
        context?: HttpContext,
    ): Promise<{
        session_key?: string;
        logout_redirect?: string;
        registration_id?: string;
    }> {
        return lastValueFrom(
            this.login$Response(params, key, context).pipe(
                map(
                    (r) =>
                        r.body as {
                            session_key?: string;
                            logout_redirect?: string;
                            registration_id?: string;
                        },
                ),
            ),
        );
    }

    /**
     * Path part for operation createUser
     */
    static readonly CreateUserPath = '/register/';

    /**
     * Register a new user
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `createUser()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    createUser$Response(
        params: {
            /**
             * Details of the new user to register
             */
            body: NewUser;
        },
        context?: HttpContext,
    ): Observable<
        StrictHttpResponse<{
            user: User;
        }>
    > {
        const rb = new RequestBuilder(
            this.rootUrl,
            UserAndAuthenticationApiClientService.CreateUserPath,
            'post',
        );
        if (params) {
            rb.body(params.body, 'application/json');
        }

        return this.http
            .request(
                rb.build({
                    responseType: 'json',
                    accept: 'application/json',
                    context: context,
                }),
            )
            .pipe(
                filter((r) => r instanceof HttpResponse),
                map((r) => {
                    return r as StrictHttpResponse<{
                        user: User;
                    }>;
                }),
            );
    }

    /**
     * Register a new user
     *
     * This method provides access only to the response body.
     * To access the full response (for headers, for example), `createUser$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    createUser(
        params: {
            /**
             * Details of the new user to register
             */
            body: NewUser;
        },
        context?: HttpContext,
    ): Promise<{
        user: User;
    }> {
        return lastValueFrom(
            this.createUser$Response(params, context).pipe(
                map(
                    (
                        r: StrictHttpResponse<{
                            user: User;
                        }>,
                    ) =>
                        r.body as {
                            user: User;
                        },
                ),
            ),
        );
    }

    /**
     * Path part for operation getCurrentUser
     */
    static readonly GetCurrentUserPath = '/profile/';

    /**
     * Get current user.
     *
     * Gets the currently logged-in user
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getCurrentUser()` instead.
     *
     * This method doesn't expect any request body.
     */
    getCurrentUser$Response(
        params?: object,
        context?: HttpContext,
    ): Observable<
        StrictHttpResponse<{
            user: LoggedInUser;
        }>
    > {
        const rb = new RequestBuilder(
            this.rootUrl,
            UserAndAuthenticationApiClientService.GetCurrentUserPath,
            'get',
        );

        return this.http
            .request(
                rb.build({
                    responseType: 'json',
                    accept: 'application/json',
                    context: context,
                }),
            )
            .pipe(
                filter((r) => r instanceof HttpResponse),
                map((r) => {
                    return r as StrictHttpResponse<{
                        user: LoggedInUser;
                    }>;
                }),
            );
    }

    /**
     * Get current user.
     *
     * Gets the currently logged-in user
     *
     * This method provides access only to the response body.
     * To access the full response (for headers, for example), `getCurrentUser$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getCurrentUser(
        params?: object,
        context?: HttpContext,
    ): Promise<{
        user: LoggedInUser;
    }> {
        return lastValueFrom(
            this.getCurrentUser$Response(params, context).pipe(
                map(
                    (
                        r: StrictHttpResponse<{
                            user: LoggedInUser;
                        }>,
                    ) =>
                        r.body as {
                            user: LoggedInUser;
                        },
                ),
            ),
        );
    }
}
